import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createEulaProfileFormSchema,
  type EulaProfileFormValues,
} from './validation/eulaProfileSchema';
import { useTranslation } from 'react-i18next';
import { useVerificationProfile } from '../api/queries/useVerificationProfile';
import { useQueryClient } from '@tanstack/react-query';
import type { VerificationProfileResponseDto } from '../api/types';
import { useSessionStore } from '@/entities/session';
import { useEffect } from 'react';

const useEulaProfileForm = (
  skipEula: boolean = false,
  initialValues?: EulaProfileFormValues
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { session } = useSessionStore();
  const schema = createEulaProfileFormSchema(t, skipEula);

  useVerificationProfile();

  const form = useForm<EulaProfileFormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: initialValues
      ? initialValues
      : async () => {
          const userVerificationProfile = (await queryClient.ensureQueryData({
            queryKey: ['user-verification-profile', session?.userId],
          })) as VerificationProfileResponseDto;
          return {
            firstName: userVerificationProfile?.firstName || '',
            lastName: userVerificationProfile?.lastName || '',
            middleName: userVerificationProfile?.middleName || '',
            dateOfBirth: userVerificationProfile?.dateOfBirth || '',
            citizenship: userVerificationProfile?.citizenship || '',
            checkBoxesResult: [],
          };
        },
  });

  useEffect(() => {
    if (initialValues?.dateOfBirth) {
      form.setValue('dateOfBirth', initialValues.dateOfBirth, {
        shouldValidate: true,
      });
    }
  }, [initialValues]);

  return {
    form,
  };
};

export { useEulaProfileForm };
